import { useState } from 'react';
import { useToast } from 'services/toast';
import { ToastType } from 'services/toast/types';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { useRecoilValue } from 'recoil';
import { generatePortalUrl } from 'services/integration/api';
import { typeformRecordSelector } from '../atoms/typeformRecords';
import { TypeformRecordType } from 'generated-graphql/types';
import { SxProps, Theme } from '@mui/material';

const typeformSuccessToastSx: SxProps<Theme> = {
  width: '600px',
  maxWidth: '90vw',
  '& .MuiAlert-action': {
    color: 'primary.main'
  }
};

interface UsePortalUrlProps {
  candidateBatchId?: string;
  campaignId?: string;
}

interface UsePortalUrlReturn {
  portalUrl: string;
  isGeneratingPortalUrl: boolean;
  onGeneratePortalUrl: () => Promise<void>;
  onCopyPortalUrl: () => Promise<void>;
  canCopyPortalUrl: boolean;
}

export const usePortalUrl = ({
  candidateBatchId,
  campaignId
}: UsePortalUrlProps): UsePortalUrlReturn => {
  const [portalUrl, setPortalUrl] = useState<string>('');
  const [isGeneratingPortalUrl, setIsGeneratingPortalUrl] = useState(false);
  const typeformRecordAnalysis = useRecoilValue(
    typeformRecordSelector(TypeformRecordType.Analysis)
  );

  const typeformRecordCampaign = useRecoilValue(
    typeformRecordSelector(TypeformRecordType.Campaign)
  );

  const { showToast } = useToast();
  const { track } = useTracking();

  // Determine if a Typeform record exists for copying
  const canCopyPortalUrl = (() => {
    if (campaignId) {
      return Boolean(typeformRecordCampaign?.id);
    }
    if (candidateBatchId) {
      return Boolean(typeformRecordAnalysis?.id);
    }
    return false;
  })();

  // Helper function to generate the portal URL
  const getPortalUrl = async () => {
    if (campaignId) {
      const response = await generatePortalUrl({ campaignId });
      return response.url;
    }
    if (candidateBatchId) {
      const response = await generatePortalUrl({ candidateBatchId });
      return response.url;
    }
    throw new Error('No candidate batch or campaign id provided');
  };

  // Silent generation - for pre-fetching on page load
  const onGeneratePortalUrl = async () => {
    try {
      if (campaignId && !typeformRecordCampaign?.id) {
        return;
      }

      if (candidateBatchId && !typeformRecordAnalysis?.id) {
        return;
      }

      // Already cached, skip
      if (portalUrl) {
        return;
      }

      setIsGeneratingPortalUrl(true);

      const url = await getPortalUrl();
      setPortalUrl(url);
    } catch (error) {
      // For silent pre-fetching, errors should be handled silently, rather than thrown
      console.error('Failed to pre-fetch portal URL:', error);
    } finally {
      setIsGeneratingPortalUrl(false);
    }
  };

  // User action - copies to clipboard + shows toast
  const onCopyPortalUrl = async () => {
    try {
      if (campaignId && !typeformRecordCampaign?.id) {
        return;
      }

      if (candidateBatchId && !typeformRecordAnalysis?.id) {
        return;
      }

      // If already cached, copy directly
      if (portalUrl) {
        try {
          setIsGeneratingPortalUrl(true);
          await navigator.clipboard.writeText(portalUrl);
          showToast({
            message: 'Typeform link copied to clipboard',
            type: ToastType.Success,
            sx: typeformSuccessToastSx
          });
          track(TrackingEventName.AnalyseDashboardShare);
        } catch (error) {
          console.error('Failed to copy portal URL to clipboard:', error);
          showToast({
            message:
              'We could not copy the link to clipboard. Please try again.',
            type: ToastType.Error
          });
        } finally {
          setIsGeneratingPortalUrl(false);
        }
        return;
      }

      setIsGeneratingPortalUrl(true);

      const url = await getPortalUrl();
      setPortalUrl(url);

      try {
        await navigator.clipboard.writeText(url);
        showToast({
          message: 'Typeform link copied to clipboard',
          type: ToastType.Success,
          sx: typeformSuccessToastSx
        });
        track(TrackingEventName.AnalyseDashboardShare);
      } catch (error) {
        console.error('Failed to copy portal URL to clipboard:', error);
        showToast({
          message: 'We could not copy the link to clipboard. Please try again.',
          type: ToastType.Error
        });
      }
    } catch {
      showToast({
        message:
          'We could not generate the portal link. Please contact support.',
        type: ToastType.Error
      });
    } finally {
      setIsGeneratingPortalUrl(false);
    }
  };

  return {
    portalUrl,
    isGeneratingPortalUrl,
    onGeneratePortalUrl,
    onCopyPortalUrl,
    canCopyPortalUrl
  };
};
